import {
  Component,
  Host,
  Prop,
  Element,
  State,
  Method,
  h
} from "@stencil/core";

import { MercuryBundles } from "@genexus/mercury";
import { config } from "../../common/config";
import { Locale } from "../../common/locale";
import { FormSubmitResult } from "../../common/types";
import {
  ControlValidation,
  validateControls
} from "../../common/form-validation";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tooltip",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-curl-inspector",
  styleUrl: "curl-inspector.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/curl-inspector"]
})
export class GxIdeCurlInspector {
  #componentLocale: any;
  #chShortcutsEl: HTMLChShortcutsElement;
  #descriptionEl!: HTMLChEditElement;
  #moduleEntitySelector!: HTMLGxIdeEntitySelectorElement;
  #nameEl!: HTMLChEditElement;
  #editorEl: TextProviderElement;
  #curlContentChangeHandler: EventListener;
  #keydownHandler: EventListener;

  @Element() el: HTMLGxIdeCurlInspectorElement;

  @State() showTooltipError: boolean = false;
  @State() isFormValid: boolean = false;
  @State() curlContent: string = "";
  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * Callback that must be invoked when the user wants to cancel the operation
   */
  @Prop() readonly cancelCallback: () => Promise<void>;

  /**
   * Callback that must be invoked when the user confirms the creation. Receives the data
   * necessary to create the procedure.
   */
  @Prop() readonly confirmCallback!: (
    data: CurlImportData
  ) => Promise<FormSubmitResult>;

  /**
   * Default value for Module/Folder field
   */
  @Prop() readonly defaultParent: EntityData;

  /**
   * Actual value for Module/Folder field
   */
  @Prop() readonly parent: EntityData;

  /**
   * Callback invoked when the action is executed in the Module/Folder filter. It returns the
   * information of the selected object (id and name) or 'undefined' if it was canceled.
   */
  @Prop() readonly selectModuleCallback: SelectModuleCallback;

  /**
   * Callback to validate the name. Should return true if the name is valid, false otherwise.
   */
  @Prop() readonly validateNameCallback?: (name: string) => boolean;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  componentDidLoad() {
    this.#initializeValidatableControls();
  }

  disconnectedCallback() {
    if (this.#editorEl && this.#editorEl.removeEventListener) {
      this.#editorEl.removeEventListener("input", this.#handleEditorChange);
      this.#editorEl.removeEventListener(
        "curl-content-change",
        this.#curlContentChangeHandler
      );
      this.#editorEl.removeEventListener("keydown", this.#keydownHandler);
    }
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    this.#chShortcutsEl.suspend = suspendShortcuts;
  }

  #initializeValidatableControls = () => {
    const validatableControls: HTMLElement[] = [
      this.#nameEl,
      this.#moduleEntitySelector
    ];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #validateForm = () => {
    const name = this.#nameEl?.value?.trim() || "";
    const parentId = this.#moduleEntitySelector?.value?.id || "";
    const curl = this.curlContent?.trim() || "";

    const isNameValid = this.validateNameCallback
      ? this.validateNameCallback(name)
      : Boolean(name);
    const isParentValid = Boolean(parentId);
    const isCurlValid = Boolean(curl);

    this.isFormValid = isNameValid && isParentValid && isCurlValid;
  };

  #handleEditorChange = () => {
    if (this.#editorEl) {
      this.curlContent = this.#editorEl.text;
      this.#validateForm();
    }
  };

  #handleSlotChange = (e: Event) => {
    const elements = (e.target as HTMLSlotElement).assignedElements();
    if (elements.length) {
      this.#editorEl = elements[0] as TextProviderElement;

      this.#editorEl.addEventListener("input", this.#handleEditorChange);

      this.#curlContentChangeHandler = ((event: CustomEvent) => {
        this.curlContent = event.detail.content;
        this.#handleEditorChange();
      }) as EventListener;

      this.#editorEl.addEventListener(
        "curl-content-change",
        this.#curlContentChangeHandler
      );

      this.#keydownHandler = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === "v") {
          setTimeout(() => {
            this.#handleEditorChange();
          }, 0);
        }
      };

      this.#editorEl.addEventListener("keydown", this.#keydownHandler);

      this.curlContent = this.#editorEl.text;
      this.#validateForm();
    }
  };

  #createHandler = () => {
    const data: CurlImportData = {
      name: this.#nameEl.value,
      description: this.#descriptionEl.value,
      parentId: this.#moduleEntitySelector.value?.id,
      curl: this.curlContent
    };
    this.confirmCallback(data).then((formSubmitResult: FormSubmitResult) => {
      this.validatableControls = validateControls(
        formSubmitResult,
        this.validatableControls
      );
    });
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={"./gx-ide-assets/curl-inspector/shortcuts.json"}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <header class="header field-group spacing-body dialog-header">
            <div class="field-group field-group-name-description-module">
              <label class="label name-label" htmlFor="input-name">
                {this.#componentLocale.header.nameFieldLabel}
              </label>
              <ch-edit
                class="input name-input"
                id="input-name"
                ref={(el: HTMLChEditElement) =>
                  (this.#nameEl = el as HTMLChEditElement)
                }
                onInput={this.#validateForm}
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#nameEl)}

              <label
                class="label description-label"
                htmlFor="input-description"
              >
                {this.#componentLocale.header.descriptionFieldLabel}
              </label>
              <ch-edit
                class="input description-input"
                id="input-description"
                ref={(el: HTMLChEditElement) =>
                  (this.#descriptionEl = el as HTMLChEditElement)
                }
              ></ch-edit>

              <label class="label module-label" htmlFor="input-module">
                {this.#componentLocale.header.moduleFolderLabel}
              </label>
              <gx-ide-entity-selector
                class="module module-entity-selector"
                id="module"
                value={this.defaultParent}
                ref={(el: HTMLGxIdeEntitySelectorElement) =>
                  (this.#moduleEntitySelector =
                    el as HTMLGxIdeEntitySelectorElement)
                }
                labelPosition="none"
                defaultValue={this.defaultParent}
                selectEntityCallback={this.selectModuleCallback}
              ></gx-ide-entity-selector>
              {this.#evaluateTooltipRender(this.#moduleEntitySelector)}
            </div>
          </header>
          <div class="main field-group scrollable">
            <slot
              name="CurlTextEditor"
              onSlotchange={this.#handleSlotChange}
            ></slot>
          </div>
          <footer class="dialog-footer dialog-footer spacing-body-block-end spacing-body-inline">
            <div class="buttons-spacer">
              <button
                class="button-secondary"
                id="button-cancel"
                onClick={this.cancelCallback}
                part="button-cancel"
              >
                {this.#componentLocale.footer.btnCancel}
              </button>
              <button
                class="button-primary"
                id="button-ok"
                onClick={this.#createHandler}
                part="button-ok"
                disabled={!this.isFormValid}
              >
                {this.#componentLocale.footer.btnCreate}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type CurlImportData = {
  name: string;
  description: string;
  parentId: string;
  curl: string;
};

export type EntityData = {
  id: string;
  name: string;
  iconSrc?: string;
};

export type SelectModuleCallback = () => Promise<EntityData | undefined>;

interface ItextProvider {
  readonly text: string;
}

type TextProviderElement = HTMLElement & ItextProvider;
